import type { ActionParameterValue } from "@repo/api";
import { SwitchField } from "@repo/ui/form-fields";
import { useMemo, useRef } from "react";
import { useAppForm } from "@/shared/forms/app-form";
import type { ActivityParameter } from "@/shared/lib/template-utils";

interface ParameterConfiguratorProps {
  parameters: ActivityParameter[];
  values: ActionParameterValue[];
  onChange: (values: ActionParameterValue[]) => void;
}

/** Types that require user configuration when assigning an procedure */
const CONFIGURABLE_TYPES = new Set(["choice", "boolean", "number", "decimal", "text"]);

function buildDefaults(params: ActivityParameter[], values: ActionParameterValue[]): Record<string, string> {
  return Object.fromEntries(
    params.map((p) => {
      const existing = values.find((v) => v.name === p.name);
      if (existing) return [p.name, existing.code];
      if (p.type === "boolean") return [p.name, "false"];
      return [p.name, ""];
    }),
  );
}

export function ParameterConfigurator({ parameters, values, onChange }: ParameterConfiguratorProps) {
  const configurableParams = useMemo(
    () => parameters.filter((p) => CONFIGURABLE_TYPES.has(p.type) && (p.type !== "choice" || p.options.length > 0)),
    [parameters],
  );

  const initialDefaults = useRef(buildDefaults(configurableParams, values)).current;

  const form = useAppForm({
    defaultValues: initialDefaults,
    onSubmit: () => {},
  });

  if (configurableParams.length === 0) return null;

  const handleChange = (param: ActivityParameter, rawValue: string) => {
    const next = values.filter((v) => v.name !== param.name);
    if (rawValue) {
      if (param.type === "choice") {
        const option = param.options.find((o) => o.code === rawValue);
        if (option) next.push({ name: param.name, code: option.code, display: option.display });
      } else if (param.type === "boolean") {
        next.push({ name: param.name, code: rawValue, display: rawValue === "true" ? "Yes" : "No" });
      } else {
        next.push({ name: param.name, code: rawValue, display: rawValue });
      }
    }
    onChange(next);
  };

  return (
    <div className="space-y-3">
      {configurableParams.map((param) => {
        if (param.type === "choice") {
          return (
            <form.AppField
              key={param.name}
              name={param.name}
              listeners={{ onChange: ({ value }) => handleChange(param, value) }}
            >
              {(field) => (
                <field.SelectField
                  label={param.text}
                  placeholder="Select..."
                  options={param.options.map((o) => ({ value: o.code, label: o.display }))}
                />
              )}
            </form.AppField>
          );
        }

        if (param.type === "boolean") {
          return (
            <form.AppField
              key={param.name}
              name={param.name}
              listeners={{ onChange: ({ value }) => handleChange(param, value) }}
            >
              {(field) => (
                <SwitchField
                  label={param.text}
                  value={field.state.value === "true"}
                  onChange={(checked) => field.handleChange(checked ? "true" : "false")}
                  onBlur={field.handleBlur}
                />
              )}
            </form.AppField>
          );
        }

        return (
          <form.AppField
            key={param.name}
            name={param.name}
            listeners={{ onChange: ({ value }) => handleChange(param, value) }}
          >
            {(field) =>
              param.type === "number" || param.type === "decimal" ? (
                <field.StringNumberField label={param.text} step={param.type === "number" ? 1 : "any"} />
              ) : (
                <field.InputField label={param.text} />
              )
            }
          </form.AppField>
        );
      })}
    </div>
  );
}
